import { styled } from '@mui/system'
import store, { AllState } from '../../../store'
import {
  Comment,
  CommentDataType,
  Reaction,
  deleteComment,
  generateEditingCommentUuidKey,
  startEditComment,
  addReaction,
  deleteReaction,
} from '../../../store/comments'
import Auth from '../../../lib/commons/auth'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import React from 'react'
import IconButton from '@mui/material/IconButton'
import MoreVertIcon from '@mui/icons-material/MoreVertRounded'
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Popper from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import AlertDialog from '../../components/dialogs/AlertDialog'
import CommentEditor from './CommentEditor'
import MarkupViewer from '../../components/viewers/MarkupViewer'
import { connect } from 'react-redux'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { FontSize } from '../../../styles/commonStyles'
import './styles.scss'
import CommentMessage from './CommentMessage'
import FileViewerDialog from '../../components/dialogs/FileViewerDialog'
import { FileType } from '../../../utils/attachment'
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'
import { colorPalette } from '../../style/colorPalette'
import { featureFlagService } from '../../../utils/feature-flag'

interface ComponentOwnProps {
  applicationFunctionUuid: string
  dataUuid?: string
  commentDataType: CommentDataType
  suffix?: string
  projectUuid?: string
  key: string
  comment: Comment
  index: any
  isCreatedByLoginUser: boolean
  // Use to avoid edit more than two comments at the same time.
  checkIsMenuOpen: Function
  setIsMenuOpen: Function
  hasEditingComment: boolean
}

interface StateProps {
  isEditing: boolean
}

interface Props extends WrappedComponentProps, ComponentOwnProps, StateProps {}

const RootContainer = styled('div')<{
  isEditing: boolean
  isHovered: boolean
}>(({ isEditing, isHovered }) => {
  if (isEditing) {
    return {
      margin: '0 0 8px',
      padding: '8px 0',
      backgroundColor: '#f2c7441a',
      '&:first-child': {
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px',
      },
      '&:last-child': {
        marginBottom: 0,
        borderBottomLeftRadius: '10px',
        borderBottomRightRadius: '10px',
      },
    }
  }
  return {
    backgroundColor: isHovered ? '#f7f7f7' : undefined,
    position: 'relative',
    width: 'auto',
    fontSize: FontSize.MEDIUM,
    padding: '4px 4px',
    margin: '0px 4px',
    '&:first-child': {
      marginTop: '4px',
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px',
    },
    '&:last-child': {
      marginBottom: '4px',
      borderBottomLeftRadius: '4px',
      borderBottomRightRadius: '4px',
    },
  }
})
const ButtonsContainer = styled(Box)({
  position: 'absolute',
  top: '1px',
  right: '1px',
  display: 'flex',
  gap: '4px',
})
const AddReactionButton = styled(Box)({
  border: '1px solid #aaa',
  borderRadius: 5,
  backgroundColor: '#fff',
  lineHeight: '1em',
})
const MenuButton = styled(Box)({
  border: '1px solid #aaa',
  borderRadius: 5,
  backgroundColor: '#fff',
  lineHeight: '1em',
})
const MenuListPopper = styled(Popper)({
  zIndex: 1,
})
const StyledMenuItem = styled(MenuItem)({
  '&hover': {
    color: '#fff',
    backgroundColor: '#1264a1',
  },
})
const DeleteMenuItem = styled(MenuItem)({
  color: '#e01e5a',
  '&hover': {
    color: '#fff',
    backgroundColor: '#e01e5a',
  },
})
const ReactionsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginTop: '4px',
})
const ReactionChip = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  padding: '2px 8px',
  borderRadius: '12px',
  backgroundColor: '#f0f0f0',
  fontSize: '12px',
  cursor: 'default',
  '&:hover': {
    backgroundColor: '#e0e0e0',
  },
})

const ReactionTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: colorPalette.monotone[10],
    fontSize: '12px',
    padding: '8px 12px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#fff',
  },
})

const ReactionUserList = styled(Box)({
  maxHeight: '150px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
})

const ReactionUserItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
})

const ReactionUserAvatar = styled(Avatar)({
  width: '18px',
  height: '18px',
})

const toUnifiedCode = (emoji: string): string => {
  return [...emoji].map(char => char.codePointAt(0)?.toString(16)).join('-')
}

interface State {
  isHovered: boolean
  anchorEl: any
  reactionPickerAnchorEl: any
  isAlertDialogOpen: boolean
  fileViewerDialogState: {
    open: boolean
    name: string
    source: string
    size?: number
    url: string
    updatedBy: string
    updatedAt?: number
  }
}

class CommentItem extends React.Component<Props, State> {
  state = {
    isHovered: false,
    anchorEl: null,
    reactionPickerAnchorEl: null,
    isAlertDialogOpen: false,
    fileViewerDialogState: {
      open: false,
      name: '',
      source: '',
      url: '',
      updatedBy: '',
    },
  }

  handleMouseEnter = () => {
    if (!this.props.checkIsMenuOpen()) {
      this.setState({ isHovered: true })
    }
  }
  handleMouseLeave = () => {
    if (!this.props.checkIsMenuOpen() && !this.state.reactionPickerAnchorEl) {
      this.setState({ isHovered: false, anchorEl: null })
    }
  }
  handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    this.setState({ anchorEl: event.currentTarget })
    this.props.setIsMenuOpen(true)
  }
  handleMenuClose = () => {
    this.setState({ anchorEl: null, isHovered: false })
    this.props.setIsMenuOpen(false)
  }
  handleReactionPickerOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    this.setState({ reactionPickerAnchorEl: event.currentTarget })
    this.props.setIsMenuOpen(true)
  }
  handleReactionPickerClose = () => {
    this.setState({ reactionPickerAnchorEl: null, isHovered: false })
    this.props.setIsMenuOpen(false)
  }
  handleEmojiClick = (emojiObject: EmojiClickData) => {
    const myReaction = this.props.comment.reactions?.find(
      r =>
        r.emoji === emojiObject.emoji &&
        r.createdBy?.uuid === Auth.getCurrentTenant()?.user?.uuid
    )

    if (myReaction) {
      store.dispatch(
        deleteReaction(
          this.props.applicationFunctionUuid,
          this.props.dataUuid!,
          this.props.comment.uuid,
          myReaction.uuid
        )
      )
    } else {
      store.dispatch(
        addReaction(
          this.props.applicationFunctionUuid,
          this.props.dataUuid!,
          this.props.comment.uuid,
          emojiObject.emoji
        )
      )
    }
    this.handleReactionPickerClose()
  }
  editMessage = () => {
    if (!this.props.hasEditingComment) {
      store.dispatch(
        startEditComment(
          this.props.applicationFunctionUuid,
          this.props.dataUuid!,
          this.props.suffix,
          this.props.comment.uuid
        )
      )
    }
    this.handleMenuClose()
  }
  handleAlertOpen = () => {
    this.setState({ isAlertDialogOpen: true })
    this.handleMenuClose()
  }
  handleAlertClose = () => {
    this.setState({ isAlertDialogOpen: false })
  }
  deleteMessage = () => {
    store.dispatch(
      deleteComment(
        this.props.applicationFunctionUuid,
        this.props.dataUuid!,
        this.props.commentDataType,
        this.props.comment.uuid
      )
    )
    this.handleAlertClose()
  }
  closeFileViewerDialog = () => {
    this.setState({
      fileViewerDialogState: {
        open: false,
        name: '',
        source: '',
        size: undefined,
        url: '',
        updatedBy: '',
        updatedAt: undefined,
      },
    })
  }
  openFileViewerDialog = ({ url, name }: { url: string; name: string }) => {
    this.setState({
      fileViewerDialogState: {
        open: true,
        name,
        source: FileType.LOCAL,
        size: undefined,
        url,
        updatedBy: this.props.comment.createdBy?.name || '',
        updatedAt: this.props.comment.createdAt,
      },
    })
  }

  groupReactions = (
    reactions: Reaction[]
  ): {
    emoji: string
    count: number
    users: { name: string; iconUrl: string }[]
    myReactionUuid?: string
  }[] => {
    const currentUserUuid = Auth.getCurrentTenant()?.user?.uuid
    const grouped: Record<
      string,
      {
        count: number
        users: { name: string; iconUrl: string }[]
        myReactionUuid?: string
      }
    > = {}

    reactions.forEach(reaction => {
      if (!grouped[reaction.emoji]) {
        grouped[reaction.emoji] = { count: 0, users: [] }
      }
      grouped[reaction.emoji].count += 1
      grouped[reaction.emoji].users.push({
        name: reaction.createdBy.name,
        iconUrl: reaction.createdBy.iconUrl,
      })
      if (reaction.createdBy?.uuid === currentUserUuid) {
        grouped[reaction.emoji].myReactionUuid = reaction.uuid
      }
    })

    return Object.entries(grouped).map(([emoji, data]) => ({
      emoji,
      count: data.count,
      users: data.users,
      myReactionUuid: data.myReactionUuid,
    }))
  }

  handleReactionClick = (emoji: string, myReactionUuid?: string) => {
    if (myReactionUuid) {
      store.dispatch(
        deleteReaction(
          this.props.applicationFunctionUuid,
          this.props.dataUuid!,
          this.props.comment.uuid,
          myReactionUuid
        )
      )
    } else {
      store.dispatch(
        addReaction(
          this.props.applicationFunctionUuid,
          this.props.dataUuid!,
          this.props.comment.uuid,
          emoji
        )
      )
    }
  }

  render() {
    const { anchorEl, reactionPickerAnchorEl } = this.state
    const open = Boolean(anchorEl)
    const reactionPickerOpen = Boolean(reactionPickerAnchorEl)
    return (
      <RootContainer
        isEditing={this.props.isEditing}
        isHovered={this.state.isHovered}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <Box display="flex" key={this.props.key}>
          {this.props.isEditing && (
            <CommentEditor
              applicationFunctionUuid={this.props.applicationFunctionUuid}
              dataUuid={this.props.dataUuid}
              commentDataType={this.props.commentDataType}
              suffix={this.props.suffix}
              projectUuid={this.props.projectUuid}
              comment={this.props.comment}
              isEditing={true}
            />
          )}
          {!this.props.isEditing && (
            <CommentMessage
              commentText={this.props.comment.text}
              onClickImage={this.openFileViewerDialog}
            />
          )}
          {!this.props.isEditing && this.state.isHovered && (
            <ButtonsContainer>
              {featureFlagService.isEnabled('comment-reaction') && (
                <AddReactionButton>
                  <IconButton
                    size={'small'}
                    onClick={this.handleReactionPickerOpen}
                  >
                    <FaceRetouchingNaturalIcon
                      color="action"
                      fontSize="small"
                    />
                  </IconButton>
                </AddReactionButton>
              )}
              {this.props.isCreatedByLoginUser && (
                <MenuButton>
                  <IconButton size={'small'} onClick={this.handleMenuOpen}>
                    <MoreVertIcon color="action" fontSize="small" />
                  </IconButton>
                </MenuButton>
              )}
            </ButtonsContainer>
          )}
          <MenuListPopper
            open={open}
            anchorEl={anchorEl}
            placement={'left'}
            disablePortal={true}
          >
            <ClickAwayListener onClickAway={this.handleMenuClose}>
              <Paper>
                <MenuList id="menu-list" style={{ paddingInlineStart: 0 }}>
                  <StyledMenuItem
                    onClick={this.editMessage}
                    disabled={this.props.hasEditingComment}
                  >
                    {this.props.intl.formatMessage({
                      id: 'commentItem.editMessage',
                    })}
                  </StyledMenuItem>
                  <DeleteMenuItem onClick={this.handleAlertOpen}>
                    {this.props.intl.formatMessage({
                      id: 'commentItem.deleteMessage',
                    })}
                  </DeleteMenuItem>
                </MenuList>
              </Paper>
            </ClickAwayListener>
          </MenuListPopper>
          {featureFlagService.isEnabled('comment-reaction') && (
            <Popper
              open={reactionPickerOpen}
              anchorEl={reactionPickerAnchorEl}
              placement={'bottom-start'}
              style={{ zIndex: 1300 }}
              popperOptions={{
                strategy: 'fixed',
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 8],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                      padding: 8,
                      altAxis: true,
                      tether: false,
                    },
                  },
                  {
                    name: 'flip',
                    options: {
                      fallbackPlacements: [
                        'top-start',
                        'bottom-end',
                        'top-end',
                      ],
                    },
                  },
                ],
              }}
            >
              <ClickAwayListener onClickAway={this.handleReactionPickerClose}>
                <Paper>
                  <EmojiPicker onEmojiClick={this.handleEmojiClick} />
                </Paper>
              </ClickAwayListener>
            </Popper>
          )}
        </Box>
        {(() => {
          if (!featureFlagService.isEnabled('comment-reaction')) return null
          const reactions = this.props.comment.reactions || []
          return reactions.length > 0 ? (
            <ReactionsContainer>
              {this.groupReactions(reactions).map(
                ({ emoji, count, users, myReactionUuid }) => (
                  <ReactionTooltip
                    key={emoji}
                    title={
                      <ReactionUserList>
                        {users.map((user, index) => (
                          <ReactionUserItem key={index}>
                            <ReactionUserAvatar src={user.iconUrl} />
                            {user.name}
                          </ReactionUserItem>
                        ))}
                      </ReactionUserList>
                    }
                    placement="bottom"
                    arrow={true}
                  >
                    <ReactionChip
                      onClick={() =>
                        this.handleReactionClick(emoji, myReactionUuid)
                      }
                      sx={{
                        cursor: myReactionUuid ? 'pointer' : 'default',
                        backgroundColor: myReactionUuid
                          ? colorPalette.skyBlue[0]
                          : '#f0f0f0',
                        borderColor: myReactionUuid
                          ? colorPalette.skyBlue[4]
                          : 'transparent',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        '&:hover': {
                          backgroundColor: myReactionUuid
                            ? colorPalette.skyBlue[0]
                            : '#e0e0e0',
                        },
                      }}
                    >
                      <img
                        src={`https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/${toUnifiedCode(emoji)}.png`}
                        alt={emoji}
                        width={14}
                        height={14}
                        style={{ verticalAlign: 'middle' }}
                        onError={(
                          event: React.SyntheticEvent<HTMLImageElement>
                        ) => {
                          const target = event.currentTarget
                          const text = document.createTextNode(emoji)
                          target.parentNode?.replaceChild(text, target)
                        }}
                      />
                      {count > 1 && <span>{count}</span>}
                    </ReactionChip>
                  </ReactionTooltip>
                )
              )}
            </ReactionsContainer>
          ) : null
        })()}
        <AlertDialog
          isOpen={this.state.isAlertDialogOpen}
          title={this.props.intl.formatMessage({
            id: 'commentItem.deleteMessage',
          })}
          message={this.props.intl.formatMessage({
            id: 'commentItem.deleteMessage.message',
          })}
          extraContent={<MarkupViewer content={this.props.comment.text} />}
          closeButtonTitle={this.props.intl.formatMessage({
            id: 'dialog.cancel',
          })}
          closeHandler={this.handleAlertClose}
          submitButtonTitle={this.props.intl.formatMessage({
            id: 'dialog.delete',
          })}
          submitHandler={this.deleteMessage}
        />
        <FileViewerDialog
          {...this.state.fileViewerDialogState}
          closeHandler={this.closeFileViewerDialog}
        />
      </RootContainer>
    )
  }
}

const mapStateToProps = (state: AllState, ownProps: ComponentOwnProps) => ({
  isEditing:
    ownProps.comment.uuid ===
    state.comments.editingCommentUuids.get(
      generateEditingCommentUuidKey(
        ownProps.applicationFunctionUuid,
        ownProps.dataUuid!,
        ownProps.suffix
      )
    ),
})
export default connect<StateProps, void, ComponentOwnProps, AllState>(
  mapStateToProps
)(injectIntl(CommentItem))
